import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { LuAlertTriangle } from 'react-icons/lu';
import api, { planosAPI } from '../services/api';
import { isValidCPF, validateBirthDateAge } from '../utils/validators';

const DependentsContainer = styled.div`
  margin-top: 20px;
`;

const DependentItem = styled.div`
  background: ${props => props.theme.colors.grayLight};
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  position: relative;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const DependentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const DependentTitle = styled.span`
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
`;

const RemoveButton = styled.button`
  background: ${props => props.theme.colors.error};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #dc2626;
  }
`;

const AddButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s ease;
  text-align: center;
  display: block;
  margin: 0 auto;

  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: ${props => props.theme.colors.grayDark};
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: ${props => props.theme.fonts.primary};
  transition: all 0.3s ease;
  background-color: ${props => props.theme.colors.white};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
  }
`;

const AgeInfo = styled.div`
  padding: 5px 10px;
  background: rgba(16, 185, 129, 0.1);
  border-radius: 4px;
  border-left: 3px solid ${props => props.theme.colors.successGreen};
  margin-top: 5px;
  font-size: 12px;
  color: ${props => props.theme.colors.successGreen};
  display: ${props => props.show ? 'block' : 'none'};
  
  .age-display {
    font-weight: 600;
  }
  
  .price-display {
    font-weight: 600;
  }
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-left: 4px solid #dc2626;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: fadeIn 0.3s ease;
  
  .error-icon {
    font-size: 24px;
    flex-shrink: 0;
  }
  
  .error-content {
    flex: 1;
    
    .error-title {
      font-weight: 600;
      color: #dc2626;
      margin-bottom: 4px;
      font-size: 14px;
    }
    
    .error-message {
      color: #991b1b;
      font-size: 13px;
      line-height: 1.5;
    }
  }
`;

// Valores fixos removidos - todos os valores devem vir da API

const AGE_RANGES = {
  '0-18': 'Até 18 anos',
  '19-49': '19 a 49 anos',
  '50-59': '50 a 59 anos',
  '60-69': '60 a 69 anos',
  '70-79': '70 a 79 anos',
  '80-89': '80 a 89 anos',
  '90+': 'Acima de 90 anos'
};

// Parentescos não precisam de filtro por gênero - mostrar todos

const GENDER_OPTIONS = [
  { value: '', label: 'Selecione...' },
  { value: 'M', label: 'Masculino' },
  { value: 'F', label: 'Feminino' }
];

// Função para formatar CPF
const formatCPF = (value) => {
  const numbers = value.replace(/\D/g, '');
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const DependentForm = ({ onDependentsChange, dependents = [], selectedPlan = null, onValidationChange }) => {
  // Inicializar nextId baseado nos IDs existentes
  const [nextId, setNextId] = useState(() => {
    if (dependents.length === 0) return 1;
    const maxId = Math.max(...dependents.map(dep => dep.id || 0));
    return maxId + 1;
  });
  const [pricing, setPricing] = useState(null);
  const [pricingError, setPricingError] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [birthDateErrors, setBirthDateErrors] = useState({});
  
  // Estado para parentescos
  const [relationships, setRelationships] = useState([
    { value: '', label: 'Selecione...', gender: '' }
  ]);
  const [loadingRelationships, setLoadingRelationships] = useState(true);

  // Buscar valores de planos
  useEffect(() => {
    let mounted = true;
    setPricingLoading(true);
    setPricingError(null);
    
    planosAPI
      .getValores()
      .then((res) => {
        const data = res.data?.data?.pricing || null;
        if (mounted) {
          setPricing(data);
          setPricingError(null);
        }
      })
      .catch((err) => {
        console.error('Erro ao carregar valores de planos:', err);
        if (mounted) {
          setPricingError('Não foi possível carregar os valores dos planos. Por favor, verifique sua conexão e tente novamente.');
        }
      })
      .finally(() => {
        if (mounted) {
          setPricingLoading(false);
        }
      });
    
    return () => {
      mounted = false;
    };
  }, []);

  // Buscar parentescos da API
  useEffect(() => {
    const fetchRelationships = async () => {
      try {
        setLoadingRelationships(true);
        const response = await api.get('/parentescos');
        const result = response.data;
        
        if (result.success && result.data) {
          // Mapear dados da API para formato esperado (sem filtro de gênero)
          const mappedRelationships = [
            { value: '', label: 'Selecione...', gender: '' },
            ...result.data.map(item => ({
              value: String(item.id),
              label: item.nome,
              gender: '' // Todos sem gênero - mostrar todos sempre
            }))
          ];
          
          setRelationships(mappedRelationships);
        }
      } catch (error) {
        console.error('Erro ao carregar parentescos:', error);
        // Manter o valor padrão em caso de erro
      } finally {
        setLoadingRelationships(false);
      }
    };

    fetchRelationships();
  }, []);

  // Notificar componente pai sobre mudanças nos erros de validação
  useEffect(() => {
    if (onValidationChange) {
      const hasErrors = Object.keys(birthDateErrors).length > 0;
      onValidationChange(hasErrors, birthDateErrors);
    }
  }, [birthDateErrors, onValidationChange]);

  const getDependentPriceByAge = (age) => {
    // Buscar valores da API usando a estrutura correta do pricing
    if (pricing && pricing.dependentes && selectedPlan && selectedPlan.idPlano) {
      const idPlano = String(selectedPlan.idPlano);
      const dependentesData = pricing.dependentes[idPlano];
      
      if (dependentesData && dependentesData.tiers && Array.isArray(dependentesData.tiers)) {
        // Buscar a faixa etária que corresponde à idade
        const faixa = dependentesData.tiers.find((tier) => {
          const idadeDe = typeof tier.idadeDe === 'number' ? tier.idadeDe : 0;
          const idadeAte = typeof tier.idadeAte === 'number' ? tier.idadeAte : 999;
          return age >= idadeDe && age <= idadeAte;
        });
        
        if (faixa && typeof faixa.valorReais === 'number') {
          return faixa.valorReais;
        }
      }
    }
    
    // Se não encontrou valores da API, retornar null para indicar erro
    return null;
  };

  const getAgeRangeDescription = (age) => {
    // Buscar a faixa etária diretamente dos tiers retornados pela API
    if (pricing && pricing.dependentes && selectedPlan && selectedPlan.idPlano) {
      const idPlano = String(selectedPlan.idPlano);
      const dependentesData = pricing.dependentes[idPlano];
      
      if (dependentesData && dependentesData.tiers && Array.isArray(dependentesData.tiers)) {
        // Buscar o tier que corresponde à idade
        const faixa = dependentesData.tiers.find((tier) => {
          const idadeDe = typeof tier.idadeDe === 'number' ? tier.idadeDe : 0;
          const idadeAte = typeof tier.idadeAte === 'number' ? tier.idadeAte : 999;
          return age >= idadeDe && age <= idadeAte;
        });
        
        if (faixa) {
          const idadeDe = faixa.idadeDe;
          const idadeAte = faixa.idadeAte;
          
          // Formatar a descrição baseada nos valores da API
          if (idadeAte >= 999 || idadeAte >= 9999) {
            // Faixa aberta (ex: 90+)
            return `Acima de ${idadeDe} anos`;
          } else if (idadeDe === idadeAte) {
            // Faixa única (ex: 18 anos)
            return `${idadeDe} anos`;
          } else {
            // Faixa normal (ex: 19 a 49 anos)
            return `${idadeDe} a ${idadeAte} anos`;
          }
        }
      }
    }
    
    // Fallback para valores fixos caso não encontre na API
    if (age <= 18) return AGE_RANGES['0-18'];
    if (age <= 49) return AGE_RANGES['19-49'];
    if (age <= 59) return AGE_RANGES['50-59'];
    if (age <= 69) return AGE_RANGES['60-69'];
    if (age <= 79) return AGE_RANGES['70-79'];
    if (age <= 89) return AGE_RANGES['80-89'];
    return AGE_RANGES['90+'];
  };

  const addDependent = useCallback(() => {
    const newDependent = {
      id: nextId,
      name: '',
      cpf: '',
      birthDate: '',
      gender: '',
      relationship: '',
      age: null,
      price: 0
    };
    
    const updatedDependents = [...dependents, newDependent];
    onDependentsChange(updatedDependents);
    setNextId(prev => prev + 1);
  }, [dependents, nextId, onDependentsChange]);

  const removeDependent = useCallback((id) => {
    const updatedDependents = dependents.filter(dep => dep.id !== id);
    onDependentsChange(updatedDependents);
    // Limpar erros do dependente removido
    setBirthDateErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  }, [dependents, onDependentsChange]);

  const updateDependent = useCallback((id, field, value) => {
    const updatedDependents = dependents.map(dep => {
      if (dep.id === id) {
        const updated = { ...dep, [field]: value };
        
        // Calculate age and price when birth date changes
        if (field === 'birthDate') {
          if (!value || value.trim() === '') {
            setBirthDateErrors(prev => {
              const newErrors = { ...prev };
              delete newErrors[id];
              return newErrors;
            });
            updated.age = null;
            updated.price = 0;
          } else {
            const ageCheck = validateBirthDateAge(value, { minAge: 0, maxAge: 120, required: true });

            if (!ageCheck.valid) {
              setBirthDateErrors(prev => ({
                ...prev,
                [id]: ageCheck.error
              }));
              updated.age = ageCheck.age;
              updated.price = 0;
            } else {
              updated.age = ageCheck.age;
              const price = getDependentPriceByAge(ageCheck.age);
              updated.price = price !== null ? price : 0;

              if (price === null) {
                setBirthDateErrors(prev => ({
                  ...prev,
                  [id]: pricingError
                    ? 'Não foi possível calcular o valor. Verifique sua conexão.'
                    : 'A idade está fora das faixas etárias deste plano'
                }));
              } else {
                setBirthDateErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors[id];
                  return newErrors;
                });
              }
            }
          }
        }
        
        // Format CPF when it changes
        if (field === 'cpf') {
          updated.cpf = formatCPF(value);
        }
        
        return updated;
      }
      return dep;
    });
    
    onDependentsChange(updatedDependents);
  }, [dependents, onDependentsChange, pricing, pricingError, selectedPlan]);

  const getDependentTitle = (dependent) => {
    return dependent.name.trim() || `Dependente ${dependent.id}`;
  };

  // Retorna todos os parentescos cadastrados (sem filtro por gênero)
  const getAllRelationships = () => {
    return relationships;
  };

  return (
    <DependentsContainer>
      {/* Mensagem de erro da API */}
      {pricingError && (
        <ErrorMessage>
          <LuAlertTriangle className="error-icon" size={24} />
          <div className="error-content">
            <div className="error-title">Serviço temporariamente indisponível</div>
            <div className="error-message">{pricingError}</div>
          </div>
        </ErrorMessage>
      )}
      
      {dependents.map((dependent) => (
        <DependentItem key={dependent.id}>
          <DependentHeader>
            <DependentTitle>
              {getDependentTitle(dependent)}
            </DependentTitle>
            <RemoveButton onClick={() => removeDependent(dependent.id)}>
              Remover
            </RemoveButton>
          </DependentHeader>
          
          <FormRow>
            <FormGroup>
              <Label htmlFor={`depName-${dependent.id}`}>Nome Completo *</Label>
              <Input
                type="text"
                id={`depName-${dependent.id}`}
                value={dependent.name}
                onChange={(e) => updateDependent(dependent.id, 'name', e.target.value)}
                placeholder="Nome do dependente"
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor={`depCpf-${dependent.id}`}>
                CPF <span style={{ color: '#6b7280', fontWeight: 'normal', fontSize: '13px' }}>(opcional)</span>
              </Label>
              <Input
                type="text"
                id={`depCpf-${dependent.id}`}
                value={dependent.cpf}
                onChange={(e) => updateDependent(dependent.id, 'cpf', e.target.value)}
                placeholder="000.000.000-00"
                maxLength="14"
              />
              {dependent.cpf && dependent.cpf.length === 14 && !isValidCPF(dependent.cpf) && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                  CPF inválido
                </div>
              )}
            </FormGroup>
          </FormRow>
          
          <FormRow>
            <FormGroup>
              <Label htmlFor={`depBirthDate-${dependent.id}`}>Data de Nascimento *</Label>
              <Input
                type="date"
                id={`depBirthDate-${dependent.id}`}
                value={dependent.birthDate}
                onChange={(e) => updateDependent(dependent.id, 'birthDate', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                min={new Date(new Date().setFullYear(new Date().getFullYear() - 120)).toISOString().split('T')[0]}
                required
              />
              {birthDateErrors[dependent.id] && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px', fontWeight: '500' }}>
                  {birthDateErrors[dependent.id]}
                </div>
              )}
              <AgeInfo show={dependent.age !== null && !birthDateErrors[dependent.id] && dependent.price > 0}>
                <span className="age-display">
                  {dependent.age} anos ({getAgeRangeDescription(dependent.age || 0)})
                </span> - <span className="price-display">
                  R$ {(dependent.price || 0).toFixed(2).replace('.', ',')}/mês
                </span>
              </AgeInfo>
              {dependent.age !== null && dependent.price === 0 && !birthDateErrors[dependent.id] && pricingError && (
                <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '5px', padding: '8px', background: '#fef2f2', borderRadius: '4px', borderLeft: '3px solid #dc2626', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <LuAlertTriangle size={14} /> Não foi possível calcular o valor. Verifique sua conexão.
                </div>
              )}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor={`depGender-${dependent.id}`}>Sexo *</Label>
              <Select
                id={`depGender-${dependent.id}`}
                value={dependent.gender}
                onChange={(e) => updateDependent(dependent.id, 'gender', e.target.value)}
                required
              >
                {GENDER_OPTIONS.map(gender => (
                  <option key={gender.value} value={gender.value}>
                    {gender.label}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>
          
          <FormGroup>
            <Label htmlFor={`depRelationship-${dependent.id}`}>
              Grau de Parentesco * {loadingRelationships && <span style={{ fontSize: '12px', color: '#666' }}>(carregando...)</span>}
            </Label>
            <Select
              id={`depRelationship-${dependent.id}`}
              value={dependent.relationship}
              onChange={(e) => updateDependent(dependent.id, 'relationship', e.target.value)}
              required
              disabled={loadingRelationships}
            >
              {getAllRelationships().map(rel => (
                <option key={rel.value} value={rel.value}>
                  {rel.label}
                </option>
              ))}
            </Select>
          </FormGroup>
        </DependentItem>
      ))}

      <AddButton type="button" onClick={addDependent}>
        + Adicionar Dependente
      </AddButton>
    </DependentsContainer>
  );
};

export default DependentForm;
export { AGE_RANGES };
